import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TenderMatch } from "@/pages/TenderAgent";
import { 
  isContractAwardNotice, 
  getProcedureType, 
  getProcedureTypeDescription, 
  getProcedureDetailedDescription,
  getNoticeTypeDescription,
  getNoticeTypeDetailedDescription,
  getNoticeTypeCategory
} from "@/utils/procedureTypeUtils";
import { formatDate } from "@/utils/dateFormatters";
import { Badge } from "@/components/ui/badge";
import { 
  CalendarIcon, 
  Building2, 
  FileText, 
  Award, 
  ArrowUpRight, 
  BookmarkPlus, 
  Trash2, 
  AlertCircle,
  FileEdit,
  CalendarClock,
  ShieldCheck,
  Briefcase,
  Gavel 
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TenderData, SavedTender } from "@/types/tender";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TenderStatusSelect } from "./TenderStatusSelect";
import { formatEuro } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

interface TenderCardProps {
  tender: TenderData | SavedTender;
  onStatusChange?: (tenderId: string, status: string) => void;
  onClick: () => void;
  onDelete?: (tenderId: string) => void;
  viewMode?: 'saved' | 'search';
}

export const TenderCard = ({
  tender,
  onStatusChange,
  onClick,
  onDelete,
  viewMode = 'search'
}: TenderCardProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const isSavedTenderType = (tender: TenderData | SavedTender): tender is SavedTender => {
    return (tender as SavedTender).tender_data !== undefined;
  };

  const getTenderTitle = (tender: TenderData | SavedTender): string => {
    if (isSavedTenderType(tender) && tender.tender_data) {
      if (tender.tender_data.title) {
        return tender.tender_data.title;
      }
      if (tender.tender_data.match_reasons?.["notice-title"]?.eng) {
        return tender.tender_data.match_reasons["notice-title"].eng;
      }
      if (tender.tender_data.match_reasons?.notice_title?.eng?.[0]) {
        return tender.tender_data.match_reasons.notice_title.eng[0];
      }
      if (tender.tender_data["notice-title"]?.eng) {
        return tender.tender_data["notice-title"].eng;
      }
      if (tender.tender_data["notice-title"]) {
        const availableLanguages = Object.keys(tender.tender_data["notice-title"]);
        if (availableLanguages.length > 0) {
          return tender.tender_data["notice-title"][availableLanguages[0]];
        }
      }
      if (tender.tender_data.TI?.eng) {
        return tender.tender_data.TI.eng;
      }
      if (tender.tender_data.title) {
        return tender.tender_data.title;
      }
    } else {
      if ((tender as TenderData).title) {
        return (tender as TenderData).title;
      }
      if ((tender as TenderData).match_reasons?.["notice-title"]?.eng) {
        return (tender as TenderData).match_reasons["notice-title"].eng;
      }
      if ((tender as TenderData).match_reasons?.notice_title?.eng?.[0]) {
        return (tender as TenderData).match_reasons.notice_title.eng[0];
      }
      if ((tender as TenderData)["notice-title"]?.eng) {
        return (tender as TenderData)["notice-title"].eng;
      }
      if ((tender as TenderData)["notice-title"]) {
        const availableLanguages = Object.keys((tender as TenderData)["notice-title"]);
        if (availableLanguages.length > 0) {
          return (tender as TenderData)["notice-title"][availableLanguages[0]];
        }
      }
      if ((tender as TenderData).TI?.eng) {
        return (tender as TenderData).TI.eng;
      }
    }
    return "Untitled Tender";
  };

  const getOrganization = (tender: any) => {
    if (!tender) return "Unknown Organization";
    const data = isSavedTenderType(tender) ? tender.tender_data : tender;
    if (!data) return "Unknown Organization";
    if (data.organization) return data.organization;
    if (data.buyer_name) return data.buyer_name;
    if (data.CAE_NAME) return data.CAE_NAME;
    if (data.match_reasons) {
      if (data.match_reasons["buyer-name"]) {
        const buyerName = data.match_reasons["buyer-name"];
        if (typeof buyerName === 'object' && !Array.isArray(buyerName)) {
          if (buyerName.eng) {
            if (Array.isArray(buyerName.eng) && buyerName.eng.length > 0) {
              return buyerName.eng[0];
            }
            return buyerName.eng;
          }
          const langs = Object.keys(buyerName);
          if (langs.length > 0) {
            const firstLangValue = buyerName[langs[0]];
            if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
              return firstLangValue[0];
            }
            if (firstLangValue) {
              return firstLangValue;
            }
          }
        }
        if (Array.isArray(buyerName) && buyerName.length > 0) {
          return buyerName[0];
        }
        if (typeof buyerName === 'string') {
          return buyerName;
        }
      }
      if (data.match_reasons["organisation-name-buyer"]) {
        const orgName = data.match_reasons["organisation-name-buyer"];
        if (typeof orgName === 'object' && !Array.isArray(orgName)) {
          if (orgName.eng) {
            if (Array.isArray(orgName.eng) && orgName.eng.length > 0) {
              return orgName.eng[0];
            }
            return orgName.eng;
          }
          const langs = Object.keys(orgName);
          if (langs.length > 0) {
            const firstLangValue = orgName[langs[0]];
            if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
              return firstLangValue[0];
            }
            if (firstLangValue) {
              return firstLangValue;
            }
          }
        }
      }
    }
    if (data["buyer-name"]) {
      const buyerName = data["buyer-name"];
      if (typeof buyerName === 'object' && !Array.isArray(buyerName)) {
        if (buyerName.eng) {
          if (Array.isArray(buyerName.eng) && buyerName.eng.length > 0) {
            return buyerName.eng[0];
          }
          return buyerName.eng;
        }
        const langs = Object.keys(buyerName);
        if (langs.length > 0) {
          const firstLangValue = buyerName[langs[0]];
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue[0];
          }
          if (firstLangValue) {
            return firstLangValue;
          }
        }
      }
      if (Array.isArray(buyerName) && buyerName.length > 0) {
        return buyerName[0];
      }
      if (typeof buyerName === 'string') {
        return buyerName;
      }
    }
    if (data["organisation-name-buyer"]) {
      const orgName = data["organisation-name-buyer"];
      if (typeof orgName === 'object' && !Array.isArray(orgName)) {
        if (orgName.eng) {
          if (Array.isArray(orgName.eng) && orgName.eng.length > 0) {
            return orgName.eng[0];
          }
          return orgName.eng;
        }
        const langs = Object.keys(orgName);
        if (langs.length > 0) {
          const firstLangValue = orgName[langs[0]];
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue[0];
          }
          if (firstLangValue) {
            return firstLangValue;
          }
        }
      }
    }
    if (data.AU) {
      return typeof data.AU === 'string' ? data.AU : JSON.stringify(data.AU);
    }
    return "Unknown Organization";
  };

  const getDeadline = (tender: any) => {
    if (tender.tender_data) {
      const deadlineDates = tender.tender_data.match_reasons?.["deadline-receipt-tender-date-lot"] || tender.tender_data["deadline-receipt-tender-date-lot"];
      if (Array.isArray(deadlineDates) && deadlineDates.length > 0) {
        return formatDate(deadlineDates[0]);
      }
      if (tender.tender_data.deadline_date) {
        return formatDate(tender.tender_data.deadline_date);
      }
    }
    const deadlineDates = tender["deadline-receipt-tender-date-lot"] || tender.match_reasons?.["deadline-receipt-tender-date-lot"];
    if (Array.isArray(deadlineDates) && deadlineDates.length > 0) {
      return formatDate(deadlineDates[0]);
    }
    if (tender.deadline_date) {
      return formatDate(tender.deadline_date);
    }
    return "Deadline not specified";
  };

  const getValue = (tender: any) => {
    if (tender.tender_data) {
      if (tender.tender_data.value && tender.tender_data.value !== "N/A" && tender.tender_data.value !== "Value not specified") {
        return tender.tender_data.value;
      }
      if (tender.tender_data.match_reasons?.["estimated-value-lot"] && tender.tender_data.match_reasons?.["estimated-value-cur-lot"]) {
        const value = tender.tender_data.match_reasons["estimated-value-lot"][0];
        const currency = tender.tender_data.match_reasons["estimated-value-cur-lot"][0];
        if (value && currency) {
          return `${formatEuro(value, false)} ${currency}`;
        }
      }
      if (tender.tender_data["estimated-value-lot"] && tender.tender_data["estimated-value-cur-lot"]) {
        const value = tender.tender_data["estimated-value-lot"][0];
        const currency = tender.tender_data["estimated-value-cur-lot"][0];
        if (value && currency) {
          return `${formatEuro(value, false)} ${currency}`;
        }
      }
      if (tender.tender_data.TV && tender.tender_data.TV_CUR) {
        return `${formatEuro(tender.tender_data.TV.toString(), false)} ${tender.tender_data.TV_CUR}`;
      }
      if (tender.tender_data.value) {
        if (tender.tender_data.value === "N/A") {
          return "Value not available";
        }
        const valueStr = tender.tender_data.value.toString();
        if (/\d+\s+[KMB]\s+[A-Z]{3}/.test(valueStr)) {
          return valueStr;
        }
        const parts = valueStr.split(' ');
        if (parts.length >= 1) {
          const amount = parseFloat(parts[0].replace(/[^\d.-]/g, ''));
          const currency = parts.length > 1 ? parts[1] : 'EUR';
          if (!isNaN(amount)) {
            return `${formatEuro(amount.toString(), false)} ${currency}`;
          }
        }
        return valueStr;
      }
    }
    if (tender["estimated-value-lot"] && tender["estimated-value-cur-lot"]) {
      const value = tender["estimated-value-lot"][0];
      const currency = tender["estimated-value-cur-lot"][0];
      if (value && currency) {
        return `${formatEuro(value, false)} ${currency}`;
      }
    }
    if (tender.value) {
      if (tender.value === "N/A") {
        return "Value not available";
      }
      return tender.value;
    }
    return "Value not available";
  };

  const getTenderId = (tender: any): string | undefined => {
    if (tender.publication_number) {
      return tender.publication_number;
    }
    if (isSavedTenderType(tender) && tender.tender_id) {
      return tender.tender_id;
    }
    if (tender.tender_data?.publication_number) {
      return tender.tender_data.publication_number;
    }
    if (tender["tender-identifier"]) {
      return tender["tender-identifier"];
    }
    if (tender.tender_data?.["tender-identifier"]) {
      return tender.tender_data["tender-identifier"];
    }
    if (tender.links && Array.isArray(tender.links) && tender.links.length > 0) {
      const link = tender.links[0];
      const parts = link.split('/');
      return parts[parts.length - 1];
    }
    if (tender.tender_data?.links && Array.isArray(tender.tender_data.links) && tender.tender_data.links.length > 0) {
      const link = tender.tender_data.links[0];
      const parts = link.split('/');
      return parts[parts.length - 1];
    }
    if (tender.id && typeof tender.id === 'string' && tender.id !== 'undefined') {
      return tender.id;
    }
    return undefined;
  };

  const handleSaveTender = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save tenders",
          variant: "destructive"
        });
        return;
      }
      const tenderId = getTenderId(tender);
      if (!tenderId) {
        console.error("Failed to identify tender ID:", tender);
        toast({
          title: "Error",
          description: "Failed to identify tender ID",
          variant: "destructive"
        });
        return;
      }
      console.log("Checking if tender already exists with tender_id:", tenderId);
      const {
        data: existingTenders,
        error: queryError
      } = await supabase.from('saved_tenders').select('id').eq('tender_id', tenderId);
      if (queryError) {
        console.error('Error checking existing tenders:', queryError);
        throw queryError;
      }
      if (existingTenders && existingTenders.length > 0) {
        toast({
          title: "Already saved",
          description: "This tender is already in your saved tenders",
          variant: "destructive"
        });
        return;
      }
      let tenderDataToSave: any;
      if (isSavedTenderType(tender)) {
        tenderDataToSave = tender.tender_data;
      } else {
        tenderDataToSave = tender;
      }
      console.log("Saving tender data to Supabase...");
      const {
        error
      } = await supabase.from('saved_tenders').insert({
        user_id: user.id,
        tender_data: tenderDataToSave,
        status: 'not_started'
      });
      if (error) {
        console.error('Error saving tender:', error);
        toast({
          title: "Error",
          description: "Failed to save tender: " + error.message,
          variant: "destructive"
        });
        return;
      }
      toast({
        title: "Success",
        description: "Tender saved successfully"
      });
    } catch (error) {
      console.error('Error saving tender:', error);
      toast({
        title: "Error",
        description: "Failed to save tender",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && isSavedTenderType(tender)) {
      onDelete(tender.id);
    }
  };

  const isSavedTender = viewMode === 'saved';
  const tenderId = isSavedTenderType(tender) ? tender.id : undefined;

  const procedureType = getProcedureType(tender);
  const procedureTypeDescription = procedureType ? getProcedureTypeDescription(procedureType) : null;
  const procedureDetailedDescription = procedureType ? getProcedureDetailedDescription(procedureType) : null;
  const isAwardNotice = isContractAwardNotice(tender);

  const getTedUrl = () => {
    return null;
  };

  const getNoticeType = () => {
    const tenderData = isSavedTenderType(tender) ? tender.tender_data : tender;
    if (tenderData && tenderData["notice-type"]) {
      return tenderData["notice-type"];
    }
    return null;
  };

  const noticeType = getNoticeType();
  const noticeTypeDescription = noticeType ? getNoticeTypeDescription(noticeType) : null;
  const noticeTypeDetailedDescription = noticeType ? getNoticeTypeDetailedDescription(noticeType) : null;
  const noticeTypeCategory = noticeType ? getNoticeTypeCategory(noticeType) : null;

  const getNoticeTypeIcon = () => {
    if (!noticeType) return <FileText className="h-3 w-3 mr-1" />;

    switch (noticeTypeCategory) {
      case 'award':
        return <Award className="h-3 w-3 mr-1" />;
      case 'contract':
        return <FileText className="h-3 w-3 mr-1" />;
      case 'prior':
        return <CalendarClock className="h-3 w-3 mr-1" />;
      case 'change':
        return <FileEdit className="h-3 w-3 mr-1" />;
      default:
        if (noticeType === 'veat') return <ShieldCheck className="h-3 w-3 mr-1" />;
        if (noticeType === 'subco') return <Briefcase className="h-3 w-3 mr-1" />;
        if (noticeType === 'cn-desg' || noticeType === 'can-desg') return <Gavel className="h-3 w-3 mr-1" />;
        return <FileText className="h-3 w-3 mr-1" />;
    }
  };

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors" onClick={onClick}>
      <div className="space-y-2 flex-grow">
        <h3 className="font-medium">
          {getTenderTitle(tender)}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span>{getOrganization(tender)}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            <span>Deadline: {getDeadline(tender)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span>{getValue(tender)}</span>
          </div>
        </div>
        {(procedureTypeDescription || isAwardNotice || noticeType) && <div className="flex flex-wrap gap-2 mt-2">
            {procedureTypeDescription && <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      {procedureTypeDescription}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{procedureDetailedDescription || procedureTypeDescription}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>}
              
            {isAwardNotice && <Badge variant="success" className="text-xs">
                <Award className="h-3 w-3 mr-1" />
                Contract Award
              </Badge>}
              
            {noticeType && !isAwardNotice && <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-xs">
                      {getNoticeTypeIcon()}
                      {noticeTypeDescription || noticeType}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md p-2">
                    <p className="font-medium">{noticeTypeDescription}</p>
                    {noticeTypeDetailedDescription && (
                      <p className="text-sm mt-1">{noticeTypeDetailedDescription}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>}
          </div>}
      </div>
      
      <div className="flex items-center gap-2">
        {!isSavedTender ? <Button variant="outline" size="sm" onClick={handleSaveTender} className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4" />
            {t("common.save")}
          </Button> : <>
            <Link to={`/offer-management/${tenderId}`} onClick={e => e.stopPropagation()} className="inline-block">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t("dashboard.tenderCard.manage")}
              </Button>
            </Link>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2 text-red-600 hover:text-red-700" onClick={e => e.stopPropagation()}>
                  <Trash2 className="h-4 w-4" />
                  {t("common.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    {t("dashboard.tenderCard.deleteDialog.title")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("dashboard.tenderCard.deleteDialog.description")}
                    <ul className="list-disc pl-6 mt-2">
                      <li>{t("dashboard.tenderCard.deleteDialog.documents")}</li>
                      <li>{t("dashboard.tenderCard.deleteDialog.teamMembers")}</li>
                      <li>{t("dashboard.tenderCard.deleteDialog.comments")}</li>
                      <li>{t("dashboard.tenderCard.deleteDialog.checklists")}</li>
                    </ul>
                    {t("dashboard.tenderCard.deleteDialog.warning")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={e => e.stopPropagation()}>
                    {t("common.cancel")}
                  </AlertDialogCancel>
                  <AlertDialogAction onClick={e => {
                e.stopPropagation();
                handleDelete(e);
              }} className="bg-red-600 hover:bg-red-700">
                    {t("common.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {onStatusChange && isSavedTenderType(tender) && <TenderStatusSelect status={tender.status || "not_started"} onStatusChange={value => onStatusChange(tender.id, value)} onClick={e => e.stopPropagation()} />}
          </>}
      </div>
    </div>
  );
};
